// Send-invite dialog shared by AssessmentsCard and VideoIntroCard.
// Track A7b, 6 Sep 2026. Backend: routes/assessment_invites.py (/api/assessment-invites/*).
//
// Pick channels, pick a wording, add a note, SEE EXACTLY WHAT THE CANDIDATE GETS
// (the email is the real rendered HTML in an iframe, the WhatsApp text is the real
// rendered template), then send. The Send button is disabled with a reason in
// words whenever it cannot go: flag off, template unapproved, no address on file.
//
//   <SendInviteDialog open={} onClose={} candidateId={} kind="assessment"|"video"
//                     recordId={assignmentIdOrInviteId} purpose="invite"|"reminder"
//                     onSent={reload} />
import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { AlertTriangle, Loader2, Mail, MessageCircle, Send } from 'lucide-react';

const OFF_HINT = 'Assessment invites are switched off (Settings: assessment_invites_enabled).';

export default function SendInviteDialog({ open, onClose, candidateId, kind, recordId, purpose = 'invite', onSent }) {
  const [settings, setSettings] = useState(null);
  const [templateKey, setTemplateKey] = useState('');
  const [email, setEmail] = useState(true);
  const [whatsapp, setWhatsapp] = useState(false);
  const [note, setNote] = useState('');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const timer = useRef(null);

  const idBody = useCallback(() => (kind === 'video' ? { invite_id: recordId } : { assignment_id: recordId }), [kind, recordId]);

  // settings once per open: template list, flags, whether SMTP/Interakt are configured
  useEffect(() => {
    if (!open) return;
    setError(''); setPreview(null);
    api.get('/assessment-invites/settings')
      .then((r) => {
        setSettings(r.data);
        const mine = (r.data.templates || []).filter((t) => t.purpose === purpose && (t.kind === kind || t.kind === 'both'));
        setTemplateKey((k) => (mine.some((t) => t.key === k) ? k : (r.data.defaults || {})[`${kind}:${purpose}`] || (mine[0] || {}).key || ''));
      })
      .catch((e) => setError(e?.response?.data?.detail || 'Could not load invite settings'));
  }, [open, kind, purpose]);

  // live preview, debounced on the note so typing does not hammer the server
  const refresh = useCallback(async () => {
    if (!open || !templateKey || !candidateId) return;
    setLoading(true);
    try {
      const r = await api.post('/assessment-invites/preview', {
        candidate_id: candidateId, kind, ...idBody(), template_key: templateKey,
        channels: [email && 'email', whatsapp && 'whatsapp'].filter(Boolean),
        custom_note: note || null,
      });
      setPreview(r.data); setError('');
    } catch (e) {
      setPreview(null);
      setError(e?.response?.data?.detail || 'Could not build the preview');
    } finally { setLoading(false); }
  }, [open, templateKey, candidateId, kind, idBody, note, email, whatsapp]);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(refresh, 350);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [refresh]);

  const templates = (settings?.templates || []).filter((t) => t.purpose === purpose && (t.kind === kind || t.kind === 'both'));
  const waName = preview?.whatsapp?.template;
  const waApproved = !!preview?.whatsapp?.owner_approved;

  // One place that decides whether Send may be pressed, and says why not.
  let blocked = '';
  if (settings && !settings.enabled) blocked = OFF_HINT;
  else if (!email && !whatsapp) blocked = 'Pick at least one channel.';
  else if (error) blocked = error;
  else if (!preview) blocked = 'Waiting for the preview.';
  else if (email && !preview.to_email) blocked = 'This candidate has no email address on file.';
  else if (whatsapp && !preview.to_phone) blocked = 'This candidate has no phone number on file.';
  else if (whatsapp && !waApproved) blocked = `WhatsApp template "${waName}" is not approved yet. Ruhshad approves the wording in Settings > WhatsApp centre; nothing goes out until then.`;
  else if (whatsapp && preview.whatsapp?.is_placeholder_id) blocked = `WhatsApp template "${waName}" still has a placeholder Interakt id. Paste the real id from Interakt first.`;
  else if (email && settings && !settings.smtp_configured) blocked = 'SMTP is not configured in Settings, so the email would fail.';
  else if (whatsapp && settings && !settings.interakt_configured) blocked = 'The Interakt API key is not set in Settings.';

  const send = async () => {
    setSending(true);
    try {
      const r = await api.post(`/assessment-invites/${purpose === 'reminder' ? 'remind' : 'send'}`, {
        candidate_id: candidateId, kind, ...idBody(), template_key: templateKey,
        channels: [email && 'email', whatsapp && 'whatsapp'].filter(Boolean),
        custom_note: note || null,
      });
      const parts = Object.entries(r.data.results || {}).map(([c, v]) => `${c}: ${v.ok ? 'sent' : v.error || 'failed'}`);
      if (r.data.ok) toast.success(`Sent - ${parts.join(', ')}`);
      else toast.error(`Not everything went out - ${parts.join(', ')}`);
      if (onSent) onSent();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send');
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {purpose === 'reminder' ? 'Send a reminder' : 'Send the invite'} - {kind === 'video' ? 'video introduction' : 'assessment'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={email} onChange={(e) => setEmail(e.target.checked)} />
              <Mail className="h-4 w-4" />Email
              {preview?.to_email && <span className="text-muted-foreground text-xs">{preview.to_email}</span>}
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={whatsapp} onChange={(e) => setWhatsapp(e.target.checked)} />
              <MessageCircle className="h-4 w-4" />WhatsApp
              {preview?.to_phone && <span className="text-muted-foreground text-xs">{preview.to_phone}</span>}
              {waName && <Badge variant={waApproved ? 'default' : 'destructive'}>{waApproved ? 'approved' : 'not approved'}</Badge>}
            </label>
            {loading && <Loader2 className="h-4 w-4 animate-spin ml-auto" />}
          </div>

          <div className="space-y-1">
            <div className="text-xs text-muted-foreground">Wording</div>
            <select className="border rounded-md h-9 px-2 bg-background w-full" value={templateKey}
              onChange={(e) => setTemplateKey(e.target.value)}>
              {templates.length === 0 && <option value="">No template</option>}
              {templates.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </div>

          <div className="space-y-1">
            <div className="text-xs text-muted-foreground">A note from you (optional) - appears in the email, not in the WhatsApp message</div>
            <textarea className="border rounded-md w-full p-2 bg-background min-h-16" maxLength={600} value={note}
              placeholder="e.g. Any doubts, call me on this number. - Rita"
              onChange={(e) => setNote(e.target.value)} />
          </div>

          {preview?.token_refreshed && (
            <p className="text-xs text-amber-700 flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" />The old link had expired - a fresh one has been issued for this candidate.
            </p>
          )}

          {preview?.email && email && (
            <div className="space-y-1">
              <div className="text-xs text-muted-foreground">
                Exactly what lands in the inbox - subject: <span className="text-foreground font-medium">{preview.email.subject}</span>
              </div>
              <iframe title="email preview" srcDoc={preview.email.html} sandbox=""
                className="w-full h-80 border rounded-md bg-white" />
            </div>
          )}

          {preview?.whatsapp && whatsapp && (
            <div className="space-y-1">
              <div className="text-xs text-muted-foreground">Exactly what lands on WhatsApp - template {waName}</div>
              <pre className="border rounded-md p-3 whitespace-pre-wrap bg-muted/40 text-[13px] leading-relaxed">{preview.whatsapp.body}</pre>
            </div>
          )}

          {blocked && (
            <p className="text-xs text-destructive flex items-start gap-1">
              <AlertTriangle className="h-3.5 w-3.5 mt-px shrink-0" />{blocked}
            </p>
          )}

          <div className="flex gap-2 justify-end">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button onClick={send} disabled={!!blocked || sending} title={blocked || ''}>
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1" />{purpose === 'reminder' ? 'Send reminder' : 'Send invite'}</>}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Small shared read-only list of what has already gone out, rendered under each card.
export function InviteHistory({ candidateId, kind, refreshKey }) {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (!candidateId) return;
    let dead = false;
    api.get('/assessment-invites/history', { params: { candidate_id: candidateId, limit: 20 } })
      .then((r) => { if (!dead) setRows((r.data.history || []).filter((x) => x.kind === kind)); })
      .catch(() => { /* history is a nicety; never shout about it */ });
    return () => { dead = true; };
  }, [candidateId, kind, refreshKey]);

  if (!rows.length) return null;
  return (
    <div className="border-t pt-2 space-y-1">
      <div className="text-xs text-muted-foreground">Sent so far</div>
      {rows.map((h) => (
        <div key={h.id} className="text-xs flex flex-wrap items-center gap-2">
          <Badge variant={h.ok ? 'secondary' : 'destructive'}>{h.purpose}</Badge>
          <span>{(h.channels || []).join(' + ')}</span>
          {Object.entries(h.results || {}).map(([c, v]) => (
            <span key={c} className={v.ok ? 'text-muted-foreground' : 'text-destructive'}>
              {c} {v.ok ? 'delivered' : `failed${v.error ? ` (${v.error})` : ''}`}
            </span>
          ))}
          <span className="text-muted-foreground ml-auto">
            {h.sent_by_name || 'staff'} - {h.sent_at ? new Date(h.sent_at).toLocaleString() : ''}
          </span>
        </div>
      ))}
    </div>
  );
}
